/**
 * Roles as a setting (change request 2026-11, docs/01). Every server-side permission check asks
 * for a named capability; which role holds which capability lives in `settings.roles`, seeded
 * from the client profile (default: DEFAULT_ROLES, which is exactly the docs/01 permission matrix
 * as built before this change, so nothing changes on deploy).
 *
 * Each role has a base type that never comes from capabilities:
 *   OFFICE   — Google sign-in, office layout.
 *   MECHANIC — PIN on an enrolled iPad, iPad layout, own jobs only, never any prices.
 *
 * Hard invariants no setting can break (enforced in capsFor() AND refused by validateRoles()):
 *   1. ADMIN always holds users.manage and settings.manage (nobody can lock the Admin out).
 *   2. A MECHANIC-base role can only hold MECHANIC_CAPABILITIES (so never prices).
 *   3. Void approval is always a second person (canDecideVoid, workflow.ts) — no capability
 *      lets anyone approve their own request.
 *   4. Append-only records stay append-only: there is no capability to edit or delete notes,
 *      punches history, status history or the audit log.
 */
import { z } from 'zod';
export const CAPABILITIES = [
    'jobs.viewAll',
    'jobs.create',
    'jobs.paperwork',
    'jobs.assign',
    'jobs.hold',
    'jobs.ready',
    'jobs.sendBack',
    'jobs.close',
    'jobs.reopen',
    'jobs.review',
    'parts.prices',
    'time.edit',
    'time.shop',
    'timesheets.view',
    'email.customers',
    'reports.view',
    'reports.export',
    'void.request',
    'void.approve',
    'audit.view',
    'devices.manage',
    'settings.view',
    'users.manage',
    'settings.manage',
];
export const Capability = z.enum(CAPABILITIES);
export const BASE_TYPES = ['OFFICE', 'MECHANIC'];
/** Shop-friendly descriptions for the Admin → Roles screen, grouped. */
export const CAPABILITY_INFO = {
    'jobs.viewAll': { group: 'Work orders', label: 'See all work orders, the Shop Board, customers, machines and legacy jobs' },
    'jobs.create': { group: 'Work orders', label: 'Create work orders; edit job details, customers and machines' },
    'jobs.paperwork': { group: 'Work orders', label: 'Add notes, parts, forms and customer signatures on any job; tick parts arrived' },
    'jobs.assign': { group: 'Work orders', label: 'Assign / reassign mechanics, add helpers, set queue order' },
    'jobs.hold': { group: 'Work orders', label: 'Put On Hold / Resume (mechanics: only jobs they lead)' },
    'jobs.ready': { group: 'Work orders', label: 'Mark Ready for Review (mechanics: only jobs they lead)' },
    'jobs.sendBack': { group: 'Review', label: 'Send a job back to the mechanic' },
    'jobs.close': { group: 'Review', label: 'Close a work order' },
    'jobs.reopen': { group: 'Review', label: 'Reopen a closed work order (with reason)' },
    'jobs.review': {
        group: 'Review',
        label: 'Manager decisions: resolve Additional Issues, Customer not present, rental damage, hour meter override, correct submitted forms, sign as technician, legacy cutover',
    },
    'parts.prices': { group: 'Parts', label: 'See and enter part cost / list / sell' },
    'time.edit': { group: 'Time', label: 'Add or edit time punches (with reason)' },
    'time.shop': { group: 'Time', label: 'Clock into non-billable shop time (mechanics)' },
    'timesheets.view': { group: 'Time', label: 'See and export the payroll timesheet' },
    'email.customers': { group: 'Email', label: 'Email customers (preview and send)' },
    'reports.view': { group: 'Reports', label: 'See reports' },
    'reports.export': { group: 'Reports', label: 'Export reports to CSV' },
    'void.request': { group: 'Voids', label: 'Request a void (with reason)' },
    'void.approve': { group: 'Voids', label: "Approve or deny someone else's void request" },
    'audit.view': { group: 'Admin', label: 'See the audit log' },
    'devices.manage': { group: 'Admin', label: 'Set up iPads, approve iPad requests, set mechanic PINs' },
    'settings.view': { group: 'Admin', label: 'See email templates and roles' },
    'users.manage': { group: 'Admin', label: 'Add and change users; revoke iPads' },
    'settings.manage': { group: 'Admin', label: 'Change settings, roles and email templates' },
};
/** The only capabilities a MECHANIC-base role may hold (invariant 2). */
export const MECHANIC_CAPABILITIES = ['jobs.hold', 'jobs.ready', 'time.shop'];
/** Always held by ADMIN (invariant 1). */
export const ADMIN_LOCKED = ['users.manage', 'settings.manage'];
export const BUILT_IN_ROLES = ['ADMIN', 'MANAGER', 'OFFICE', 'MECHANIC'];
export const baseOfBuiltIn = (r) => (r === 'MECHANIC' ? 'MECHANIC' : 'OFFICE');
const ALL_OFFICE = CAPABILITIES.filter((c) => c !== 'time.shop');
const MANAGER_CAPS = [
    'jobs.viewAll',
    'jobs.create',
    'jobs.paperwork',
    'jobs.assign',
    'jobs.hold',
    'jobs.ready',
    'jobs.sendBack',
    'jobs.close',
    'jobs.reopen',
    'jobs.review',
    'parts.prices',
    'time.edit',
    'timesheets.view',
    'email.customers',
    'reports.view',
    'reports.export',
    'void.request',
    'void.approve',
    'audit.view',
    'devices.manage',
    'settings.view',
];
/** Today's docs/01 matrix, exactly as built before roles became a setting. */
export const DEFAULT_ROLES = {
    ADMIN: { label: 'Admin', base: 'OFFICE', caps: ALL_OFFICE.filter((c) => !['jobs.hold', 'jobs.ready', 'jobs.sendBack'].includes(c)) },
    MANAGER: { label: 'Manager', base: 'OFFICE', caps: MANAGER_CAPS },
    OFFICE: { label: 'Office', base: 'OFFICE', caps: ['jobs.viewAll', 'jobs.create', 'jobs.paperwork', 'parts.prices', 'email.customers', 'reports.view'] },
    MECHANIC: { label: 'Mechanic', base: 'MECHANIC', caps: ['jobs.hold', 'jobs.ready', 'time.shop'] },
};
export const ROLE_KEY = /^[A-Z][A-Z0-9_]{1,39}$/;
export const RoleDefSchema = z.object({
    label: z.string().trim().min(1).max(60),
    base: z.enum(BASE_TYPES),
    caps: z.array(Capability).max(CAPABILITIES.length),
});
/**
 * A full roles setting as saved by the Admin. Refused (never silently fixed) if it would break an
 * invariant, so the Admin sees why.
 */
export const RolesSetting = z
    .record(z.string().regex(ROLE_KEY, 'Role keys are CAPITALS, digits and _'), RoleDefSchema)
    .superRefine((roles, ctx) => {
    for (const r of BUILT_IN_ROLES) {
        if (!roles[r])
            ctx.addIssue({ code: 'custom', path: [r], message: `The ${r} role can't be removed` });
        else if (roles[r].base !== baseOfBuiltIn(r))
            ctx.addIssue({ code: 'custom', path: [r, 'base'], message: `${r} must stay a ${baseOfBuiltIn(r)} role` });
    }
    for (const c of ADMIN_LOCKED) {
        if (roles.ADMIN && !roles.ADMIN.caps.includes(c))
            ctx.addIssue({ code: 'custom', path: ['ADMIN', 'caps'], message: `Admin always keeps "${c}" (so nobody is locked out)` });
    }
    for (const [key, def] of Object.entries(roles)) {
        if (def.base !== 'MECHANIC')
            continue;
        const bad = def.caps.filter((c) => !MECHANIC_CAPABILITIES.includes(c));
        if (bad.length)
            ctx.addIssue({ code: 'custom', path: [key, 'caps'], message: `Mechanic roles can't have: ${bad.join(', ')} (iPad users never see prices or office screens)` });
    }
});
/** Settings value → definitions, falling back to the defaults for anything missing or broken. */
export function readRoles(value) {
    const parsed = z.record(z.string(), RoleDefSchema).safeParse(value);
    const out = { ...DEFAULT_ROLES };
    if (!parsed.success)
        return out;
    for (const [k, v] of Object.entries(parsed.data)) {
        if (!ROLE_KEY.test(k))
            continue;
        if (BUILT_IN_ROLES.includes(k) && v.base !== baseOfBuiltIn(k))
            continue;
        out[k] = v;
    }
    return out;
}
/**
 * The capabilities a user actually has. `role` is the stored base role (the enum); `roleKey` is
 * set only for a custom role. Invariants are applied here as well as at save time, so even a
 * hand-edited settings row can't lock out the Admin or give a mechanic prices.
 */
export function capsFor(role, roleKey, roles) {
    const base = baseOfBuiltIn(role);
    const key = roleKey && !BUILT_IN_ROLES.includes(roleKey) ? roleKey : role;
    const def = roles[key] ?? (key === role ? DEFAULT_ROLES[role] : undefined);
    // A custom role that was removed (or whose base no longer matches the account) grants nothing.
    if (!def || def.base !== base)
        return role === 'ADMIN' ? [...ADMIN_LOCKED] : [];
    let caps = [...new Set(def.caps)].filter((c) => CAPABILITIES.includes(c));
    if (base === 'MECHANIC')
        caps = caps.filter((c) => MECHANIC_CAPABILITIES.includes(c));
    else
        caps = caps.filter((c) => c !== 'time.shop');
    if (key === 'ADMIN')
        for (const c of ADMIN_LOCKED)
            if (!caps.includes(c))
                caps.push(c);
    return caps;
}
export const roleLabel = (role, roleKey, roles) => roles[roleKey ?? role]?.label ?? (roleKey ?? role);
export const hasCap = (who, cap) => who.caps.includes(cap);
//# sourceMappingURL=permissions.js.map